import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { DIMENSIONI, MATRICE } from "../data/matriceCatenaValoreEn";

// Icone dimensioni (bianche) ed emoticon (colorate) — asset reali
import DimFornitori from "../assets/images/dim-fornitori.svg";
import DimOperazioni from "../assets/images/dim-operazioni.svg";
import DimSottoscrizione from "../assets/images/dim-sottoscrizione.svg";
import DimCredito from "../assets/images/dim-credito.svg";
import DimInvestimenti from "../assets/images/dim-investimenti.svg";
import DimDistributori from "../assets/images/dim-distributori.svg";
import EmoPositivo from "../assets/images/emo-positivo.svg";
import EmoNegativo from "../assets/images/emo-negativo.svg";
import EmoRischio from "../assets/images/emo-rischio.svg";
import EmoOpportunita from "../assets/images/emo-opportunita.svg";

// ===== Colori (estratti dal PDF sorgente) =====
const TEMA_HDR = "#004291";   // header "Tema"
const SOTTO_HDR = "#0961C9";  // header "Sottotema" (diverso dal Tema)

const DIM = {
  UPSTREAM:   { pill: "#924292", tint: "#E9D9E9" }, // viola
  MIDSTREAM:  { pill: "#2D8406", tint: "#D5E7CD" }, // verde
  DOWNSTREAM: { pill: "#B25600", tint: "#F4E6D9" }, // marrone
};
const CELL_BG = "#EEEEEE"; // sfondo card colonna
const GRID_LINE = "#5A5A5A"; // griglia interna celle (grigio scuro, come da PDF)
const FRAME_GRAY = "#5A5A5A"; // bordo esterno Tema/Sottotema
const ZEBRA_LIGHT = "#EEEEEE"; // riga tema chiara (E1, S1, S3, G1)
const ZEBRA_DARK = "#DEDEDE";  // riga tema scura (E4, S2, S4)

// ===== Emoticon celle (asset reali, colorate) =====
const ICONS = { P: EmoPositivo, N: EmoNegativo, R: EmoRischio, O: EmoOpportunita };

function CellIcons({ codes, s = 22 }) {
  if (!codes || codes.length === 0) return null;
  return (
    <div className="flex items-center justify-center gap-[2px]">
      {codes.map((c, i) => {
        const src = ICONS[c];
        return src ? <img key={i} src={src} alt="" style={{ width: s, height: s }} /> : null;
      })}
    </div>
  );
}

// ===== Icone header dimensioni (asset reali, bianche) =====
const DIM_ICON = {
  fornitori: DimFornitori,
  operazioniProprie: DimOperazioni,
  sottoscrizione: DimSottoscrizione,
  credito: DimCredito,
  investimenti: DimInvestimenti,
  distributori: DimDistributori,
};

// ===== Colonne (indici, indipendenti dalle larghezze) =====
const impactCol = (d) => 3 * d + 2;          // colonne 1-based del grid dimensioni
const financialCol = (d) => 3 * d + 3;
const groupCol = (d) => `${impactCol(d)} / span 2`;
const TITLE_H = 46;                            // altezza fascia titolo del box dimensioni
const Z_CARD = 0, Z_CELL = 10;

// rileva mobile (per stringere le colonne fisse)
function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const f = () => setM(window.innerWidth < bp);
    f();
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, [bp]);
  return m;
}

// Pre-calcolo righe corpo (parte da grid-row 3)
const BLOCKS = (() => {
  let gr = 3;
  const blocks = MATRICE.map((t, ti) => {
    const start = gr;
    const rows = t.sottotemi.map((st, si) => ({ st, row: gr + si }));
    gr += t.sottotemi.length;
    const dash = t.tema.indexOf("—");
    const temaCode = dash === -1 ? t.tema : t.tema.slice(0, dash).trim();   // "ESRS E1"
    const temaName = dash === -1 ? "" : t.tema.slice(dash + 1).trim();      // "Climate change"
    return { tema: t.tema, temaCode, temaName, start, span: t.sottotemi.length, rows, ti, bg: ti % 2 === 0 ? ZEBRA_LIGHT : ZEBRA_DARK };
  });
  return { blocks, bandRow: gr, lastTi: MATRICE.length - 1 };
})();

export default function MatriceCatenaValore() {
  const scrollerRef = useRef(null);
  const outerRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [rowTpl, setRowTpl] = useState(null);
  const isMobile = useIsMobile();

  // Larghezze responsive: su mobile le prime due colonne sono molto strette
  const TEMA_W = isMobile ? 52 : 128;
  const SOTTO_W = isMobile ? 78 : 160;
  const SUB_W = isMobile ? 54 : 104;
  const GAP = isMobile ? 8 : 12;
  const ICON = isMobile ? 18 : 22;
  const GROUP_W = GAP + SUB_W * 2;
  const LEFT_COLS = `${TEMA_W}px ${GAP}px ${SOTTO_W}px`;
  const RIGHT_COLS = `${GAP}px ${SUB_W}px ${SUB_W}px `.repeat(DIMENSIONI.length);
  const RIGHT_W = DIMENSIONI.length * GROUP_W;

  // Sincronizza l'altezza delle righe tra la griglia fissa e quella scrollabile.
  // ResizeObserver: ri-sincronizza a OGNI cambio dimensione (icone/font/reflow), con guard anti-loop.
  useLayoutEffect(() => {
    const L = leftRef.current, R = rightRef.current;
    if (!L || !R) return;
    const measure = (el) => getComputedStyle(el).gridTemplateRows.split(" ").map(parseFloat).filter((n) => !isNaN(n));
    let busy = false;
    const sync = () => {
      if (busy) return;
      busy = true;
      L.style.gridTemplateRows = "";
      R.style.gridTemplateRows = "";
      const la = measure(L), ra = measure(R);
      const n = Math.max(la.length, ra.length);
      if (n) {
        const merged = [];
        for (let i = 0; i < n; i++) merged.push(`${Math.round(Math.max(la[i] || 0, ra[i] || 0))}px`);
        const val = merged.join(" ");
        L.style.gridTemplateRows = val;
        R.style.gridTemplateRows = val;
        setRowTpl(val);
      }
      // rilascia il flag dopo le notifiche RO di questo ciclo (evita il loop azzera→misura→applica)
      setTimeout(() => { busy = false; }, 0);
    };
    sync();
    const ro = new ResizeObserver(() => sync());
    ro.observe(L);
    ro.observe(R);
    return () => ro.disconnect();
  }, [isMobile]);

  const { blocks, bandRow, lastTi } = BLOCKS;
  const lastRow = bandRow - 1;
  const FULL_ROWS = `1 / ${bandRow + 1}`;
  const rowStyle = rowTpl ? { gridTemplateRows: rowTpl } : undefined;

  // Su mobile la tabella scrolla in un unico contenitore (X+Y): intestazioni sticky-top
  // e colonne Tema/Sottotema sticky-left. pillH = altezza della prima riga (pillole).
  const pillH = rowTpl ? parseFloat(rowTpl) : 0;
  const stick = (top, z) => (isMobile ? { position: "sticky", top, zIndex: z } : {});

  const scrollByCols = (dir = 1) =>
    (isMobile ? outerRef : scrollerRef).current?.scrollBy({ left: dir * GROUP_W, behavior: "smooth" });

  return (
    <div className="relative w-full">
      <div className="rounded-[18px] p-3 md:p-4">
        <div
          ref={outerRef}
          className={`flex items-start ${isMobile ? "overflow-auto max-h-[70vh] overscroll-contain relative" : ""}`}
        >

          {/* ===== SINISTRA: Tema + Sottotema (fissi) ===== */}
          <div
            className={`shrink-0 ${isMobile ? "sticky left-0 z-[40] bg-[#E7E3DF]" : ""}`}
            style={{ paddingTop: TITLE_H + 1 }}
          >
            <div ref={leftRef} className="grid" style={{ gridTemplateColumns: LEFT_COLS, ...rowStyle }}>
              {/* card colonne */}
              <div className="rounded-[14px]" style={{ gridColumn: 1, gridRow: FULL_ROWS, background: "#fff", border: `1.5px solid ${FRAME_GRAY}`, zIndex: Z_CARD }} />
              <div className="rounded-[14px]" style={{ gridColumn: 3, gridRow: FULL_ROWS, background: "#fff", border: `1.5px solid ${FRAME_GRAY}`, zIndex: Z_CARD }} />
              {/* header */}
              <div className="flex items-center justify-center text-white text-[14px] md:text-[18px] font-bold rounded-t-[14px]" style={{ gridColumn: 1, gridRow: "1 / 3", background: TEMA_HDR, zIndex: Z_CELL, ...stick(0, 60) }}>Theme</div>
              <div className="flex items-center justify-center text-white text-[14px] md:text-[18px] font-bold rounded-t-[14px]" style={{ gridColumn: 3, gridRow: "1 / 3", background: SOTTO_HDR, zIndex: Z_CELL, ...stick(0, 60) }}>Subtheme</div>
              {/* corpo */}
              {blocks.map((b) => (
                <React.Fragment key={`L-${b.start}`}>
                  <div className="flex items-center [overflow-wrap:anywhere] text-[11px] md:text-[13px] font-bold px-1.5 md:px-2 py-2 leading-tight" style={{ gridColumn: 1, gridRow: `${b.start} / span ${b.span}`, background: b.bg, color: TEMA_HDR, borderBottom: b.ti === lastTi ? "none" : `1px solid ${GRID_LINE}`, zIndex: Z_CELL }}>
                    <span>{b.temaCode}{b.temaName && <><br />{b.temaName}</>}</span>
                  </div>
                  {b.rows.map(({ st, row }) => (
                    <div key={`Ls-${row}`} className="flex items-center [overflow-wrap:anywhere] text-[#1c3b5e] text-[10px] md:text-[12px] px-1.5 md:px-2 py-2 leading-tight" style={{ gridColumn: 3, gridRow: row, background: b.bg, borderBottom: row === lastRow ? "none" : `1px solid ${GRID_LINE}`, zIndex: Z_CELL }}>
                      {st.sottotema === "(single topic)" ? "" : st.sottotema}
                    </div>
                  ))}
                </React.Fragment>
              ))}
              {/* cappucci inferiori */}
              <div className="rounded-b-[14px]" style={{ gridColumn: 1, gridRow: bandRow, background: TEMA_HDR, zIndex: Z_CELL }} />
              <div className="rounded-b-[14px]" style={{ gridColumn: 3, gridRow: bandRow, background: SOTTO_HDR, zIndex: Z_CELL }} />
            </div>
          </div>

          {/* ===== DESTRA: box bordato con titolo + colonne scrollabili ===== */}
          <div className={`relative flex-1 min-w-0 ml-2 md:ml-3 rounded-[14px] border border-[#d8d8d8] ${isMobile ? "" : "overflow-hidden"}`}>
            {/* Titolo in testa al box, centrato */}
            <div className="flex items-center justify-center text-center px-2 leading-tight text-[#0E3761] font-semibold text-[12px] md:text-[17px] border-b border-[#ececec]" style={{ height: TITLE_H }}>
              Value chain dimension
            </div>

            {/* Frecce slider (nascoste su mobile: swipe nativo) */}
            <button type="button" onClick={() => scrollByCols(-1)} className={`absolute left-1 top-1/2 -translate-y-1/2 z-50 w-8 h-8 md:w-9 md:h-9 items-center justify-center rounded-full bg-white/85 shadow-md ${isMobile ? "hidden" : "flex"}`} aria-label="Scroll left">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#002655" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5 L8 12 L15 19" /></svg>
            </button>
            <button type="button" onClick={() => scrollByCols(1)} className={`absolute right-1 top-1/2 -translate-y-1/2 z-50 w-8 h-8 md:w-9 md:h-9 items-center justify-center rounded-full bg-white/85 shadow-md ${isMobile ? "hidden" : "flex"}`} aria-label="Scroll right">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#002655" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5 L16 12 L9 19" /></svg>
            </button>

            {/* Scroller orizzontale (clippa entro il box) */}
            <div ref={scrollerRef} className={`overscroll-x-contain ${isMobile ? "" : "overflow-x-auto"}`} style={{ scrollbarWidth: "thin" }}>
              <div ref={rightRef} className="grid" style={{ gridTemplateColumns: RIGHT_COLS, width: RIGHT_W, ...rowStyle }}>
                {/* card colonne */}
                {DIMENSIONI.map((d, di) => (
                  <div key={`Rc-${d.key}`} className="rounded-[14px]" style={{ gridColumn: groupCol(di), gridRow: FULL_ROWS, background: CELL_BG, border: `1.5px solid ${DIM[d.banda].pill}`, zIndex: Z_CARD }} />
                ))}
                {/* riga 1: pillole */}
                {DIMENSIONI.map((d, di) => (
                  <div key={`Rp-${d.key}`} className="flex flex-col items-center justify-center text-white rounded-t-[14px] px-1 md:px-2 py-2 leading-tight" style={{ gridColumn: groupCol(di), gridRow: 1, background: DIM[d.banda].pill, zIndex: Z_CELL, ...stick(0, 30) }}>
                    <img src={DIM_ICON[d.key]} alt="" style={{ width: ICON + 8, height: ICON + 8 }} />
                    <span className="text-[12px] md:text-[18px] font-bold text-center mt-1 leading-tight">{d.label}</span>
                  </div>
                ))}
                {/* riga 2: Impact / Financial */}
                {DIMENSIONI.map((d, di) => (
                  <React.Fragment key={`Rl-${d.key}`}>
                    <div className="flex items-center justify-center text-[#0E3761] text-[10px] md:text-[13px] font-semibold py-1" style={{ gridColumn: impactCol(di), gridRow: 2, background: DIM[d.banda].tint, borderRight: `1px solid ${GRID_LINE}`, borderBottom: `1px solid ${GRID_LINE}`, zIndex: Z_CELL, ...stick(pillH, 30) }}>Impact</div>
                    <div className="flex items-center justify-center text-[#0E3761] text-[10px] md:text-[13px] font-semibold py-1" style={{ gridColumn: financialCol(di), gridRow: 2, background: DIM[d.banda].tint, borderBottom: `1px solid ${GRID_LINE}`, zIndex: Z_CELL, ...stick(pillH, 30) }}>Financial</div>
                  </React.Fragment>
                ))}
                {/* corpo */}
                {blocks.map((b) => b.rows.map(({ st, row }) => (
                  <React.Fragment key={`Rb-${row}`}>
                    {DIMENSIONI.map((d, di) => {
                      const cell = st.celle[d.key];
                      if (cell === "NA") {
                        return (
                          <div key={`Rna-${row}-${d.key}`} className="flex items-center justify-center text-[9px] md:text-[12px] italic text-[#8a8a8a] px-0.5 text-center leading-tight" style={{ gridColumn: groupCol(di), gridRow: row, background: b.bg, borderBottom: row === lastRow ? "none" : `1px solid ${GRID_LINE}`, zIndex: Z_CELL }}>
                            not applicable
                          </div>
                        );
                      }
                      return (
                        <React.Fragment key={`Rcc-${row}-${d.key}`}>
                          <div className="flex items-center justify-center px-0.5 md:px-1 py-2" style={{ gridColumn: impactCol(di), gridRow: row, background: b.bg, borderBottom: row === lastRow ? "none" : `1px solid ${GRID_LINE}`, borderRight: `1px solid ${GRID_LINE}`, zIndex: Z_CELL }}>
                            <CellIcons codes={cell.impact} s={ICON} />
                          </div>
                          <div className="flex items-center justify-center px-0.5 md:px-1 py-2" style={{ gridColumn: financialCol(di), gridRow: row, background: b.bg, borderBottom: row === lastRow ? "none" : `1px solid ${GRID_LINE}`, zIndex: Z_CELL }}>
                            <CellIcons codes={cell.financial} s={ICON} />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                )))}
                {/* bande */}
                <div className="flex items-center justify-center text-white text-[10px] md:text-[13px] font-bold rounded-b-[14px] py-[7px]" style={{ gridColumn: groupCol(0), gridRow: bandRow, background: DIM.UPSTREAM.pill, zIndex: Z_CELL }}>UPSTREAM</div>
                <div className="flex items-center justify-center text-white text-[10px] md:text-[13px] font-bold rounded-b-[14px] py-[7px]" style={{ gridColumn: groupCol(1), gridRow: bandRow, background: DIM.MIDSTREAM.pill, zIndex: Z_CELL }}>MIDSTREAM</div>
                <div className="flex items-center justify-center text-white text-[10px] md:text-[13px] font-bold rounded-b-[14px] py-[7px]" style={{ gridColumn: `${impactCol(2)} / ${financialCol(5) + 1}`, gridRow: bandRow, background: DIM.DOWNSTREAM.pill, zIndex: Z_CELL }}>DOWNSTREAM</div>
              </div>
            </div>
          </div>
        </div>

        {/* Legenda */}
        <div className="flex flex-wrap items-center gap-x-7 gap-y-2 mt-5">
          <div className="flex items-center gap-2"><img src={EmoPositivo} alt="" className="w-[22px] h-[22px]" /><span className="text-[#0E3761] text-[14px] md:text-[16px]">Positive</span></div>
          <div className="flex items-center gap-2"><img src={EmoNegativo} alt="" className="w-[22px] h-[22px]" /><span className="text-[#0E3761] text-[14px] md:text-[16px]">Negative</span></div>
          <div className="flex items-center gap-2"><img src={EmoOpportunita} alt="" className="w-[22px] h-[22px]" /><span className="text-[#0E3761] text-[14px] md:text-[16px]">Opportunity</span></div>
          <div className="flex items-center gap-2"><img src={EmoRischio} alt="" className="w-[22px] h-[22px]" /><span className="text-[#0E3761] text-[14px] md:text-[16px]">Risk</span></div>
        </div>
      </div>
    </div>
  );
}
